import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { VendorView } from '@trackly/shared';
import { createDefaultVendor } from '../../../services/VendorService/vendor';
import { vendorApi } from '../../../services/VendorService/vendorApiBase';
import * as vt from './vendorTransforms';
import type { VendorState } from './vendorTransforms';

const initialState: VendorState = {
  currentVendor: null,
  selectedVendorId: null,
  isEdited: false,
  editedFields: [],
  filters: {
    searchTerm: '',
  },
};

const vendorSlice = createSlice({
  name: 'vendor',
  initialState,
  reducers: {
    updateVendor: (state, action: PayloadAction<Partial<VendorView>>) => {
      vt.applyVendorUpdate(state, action.payload);
    },

    initializeVendor: (state) => {
      state.currentVendor = createDefaultVendor();
      vt.resetEditState(state);
    },

    clearVendor: (state) => {
      vt.clearVendorState(state);
    },

    setSelectedVendorId: (state, action: PayloadAction<string | null>) => {
      state.selectedVendorId = action.payload;
    },

    updateVendorIdentification: (state, action: PayloadAction<{
      companyName?: string;
      dba?: string;
      status?: string;
    }>) => {
      vt.applyIdentificationUpdate(state, action.payload);
    },

    updateVendorContact: (state, action: PayloadAction<{
      primaryContactName?: string;
      primaryPhone?: string;
      primaryEmail?: string;
      secondaryContactName?: string;
      secondaryPhone?: string;
      secondaryEmail?: string;
      website?: string;
      street?: string;
      city?: string;
      state?: string;
      zip?: string;
    }>) => {
      vt.applyContactUpdate(state, action.payload);
    },

    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.filters.searchTerm = action.payload;
    },

    clearFilters: (state) => {
      state.filters = { searchTerm: '' };
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        vendorApi.endpoints.getVendorById.matchFulfilled,
        (state, action) => {
          state.currentVendor = action.payload;
          state.selectedVendorId = action.payload.id;
          vt.resetEditState(state);
        },
      )
      .addMatcher(
        vendorApi.endpoints.upsertVendor.matchFulfilled,
        (state, action) => {
          state.currentVendor = action.payload;
          if (action.payload.id) {
            state.selectedVendorId = action.payload.id;
          }
          vt.resetEditState(state);
        },
      );
  },
});

export const {
  updateVendor,
  initializeVendor,
  clearVendor,
  setSelectedVendorId,
  updateVendorIdentification,
  updateVendorContact,
  setSearchTerm,
  clearFilters,
} = vendorSlice.actions;

export default vendorSlice.reducer;
